import { OfferPageState, OfferPageDispatchAction, OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { ReducerMiddleware } from 'lib/redux/reducerUtils'
import React, { useEffect, useState } from 'react'
import { getInitialLeOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { getNextFilterWithCapacityCheckInMiddlware } from '../utils'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import useMountedEffect from 'hooks/useMountedEffect'
import { offerPageDurationChangeEvent, offerPageGuestSelectionEvent, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import { InViewHookResponse } from 'react-intersection-observer'
import PropertyContent from './PropertyContent'
import propertyMobileFilterMiddleware from './PropertyMobileFilterMiddleware'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'

const mobileVariantMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [propertyMobileFilterMiddleware]
const defaultMiddlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterWithCapacityCheckInMiddlware]
const defaultRooms = [generateRoomOccupants()]

interface Props {
  offer: App.HotelOffer;
  shouldUseLereStickyFooter: boolean;
  lereYmalStickyFooterShowTrigger: InViewHookResponse;
}

function PropertyContainer(props: Props) {
  const { offer } = props
  const [middlewares, setMiddlewares] = useState(defaultMiddlewares)
  const defaults = useAppSelector(state => getInitialLeOfferPageState(state, offer))

  const isMobileDevice = useIsMobileDevice()
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
    flights: offer.bundledWithFlightsOnly,
  }, middlewares)

  useSyncHotelTripItem(state, offer)
  const isNewMobileBookingFlowEnabled = useOptimizelyExperiment(OptimizelyExperiments.croAlwaysOnNewMobileBooking, isMobileDevice)

  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  useEffect(() => {
    if (useNewMobileBookingFlow) {
      setMiddlewares(mobileVariantMiddlewares)
    } else {
      setMiddlewares(defaultMiddlewares)
    }
  }, [useNewMobileBookingFlow])

  useMountedEffect(() => {
    // the first loaded offer from an SSR is a summary. We then fetch the full version
    // This ensures the offer page state has our latest version
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useMountedEffect(() => {
    Analytics.trackEvent(offerPageDurationChangeEvent(offer, state))
  }, [state.duration])

  useMountedEffect(() => {
    if (state.rooms) {
      Analytics.trackEvent(offerPageGuestSelectionEvent(offer, state))
    }
  }, [state.rooms])

  const hotLead = useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    checkInDate: state.checkInDate,
    duration: state.duration,
    occupancy: state.rooms,
    defaultOccupancy: defaultRooms,
  })

  useEffect(() => {
    if (hotLead?.checkIn && hotLead.checkOut) {
      Analytics.trackEvent(offerPageHotLeadEvent(offer, state))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  return <OfferPageStateProvider state={state} dispatch={stateDispatch}>
    <PropertyContent
      offer={offer}
    />
  </OfferPageStateProvider>
}

export default React.memo(PropertyContainer)
